/**
 * @file Defines constants for GRN (Goods Received Note) types.
 */
import paths from 'routes/paths';

export const GRN_TYPE = {
  PURCHASE_ORDER: 'purchase_order',
  GATE_PASS: 'gate_pass',
};

/**
 * Returns a human-readable label for a given GRN type.
 * @param {string} type - The GRN type (e.g., 'purchase_order', 'gate_pass').
 * @returns {string} The human-readable label.
 */
export const getGrnTypeLabel = type => {
  switch (type) {
    case GRN_TYPE.PURCHASE_ORDER:
      return 'Purchase Order';
    case GRN_TYPE.GATE_PASS:
      return 'Gate Pass';
    default:
      return type; // Fallback to the original type if not recognized
  }
};

/**
 * Derives the GRN type from a GRN detail response,
 * which has no grn_type field (unlike the list response).
 * @param {object} grn - The GRN detail response.
 * @returns {string|null} The GRN type, or null if it cannot be determined.
 */
export const getGrnTypeFromDetail = grn => {
  if (grn?.gate_pass?.gate_pass_code) return GRN_TYPE.GATE_PASS;
  if (grn?.purchase_order?.po_code) return GRN_TYPE.PURCHASE_ORDER;
  return null;
};

/**
 * Resolves the GRN source (PO or Gate Pass) code and its detail page path.
 * Works for both list rows (with grn_type) and detail responses (without).
 * @param {object} grn - A GRN list row or detail response.
 * @param {string} tenant - Tenant domain slug.
 * @param {string} warehouse - Warehouse domain slug.
 * @returns {{isGatePassGrn: boolean, sourceCode: string|undefined, sourceDetailPath: string}}
 */
export const getGrnSourceInfo = (grn, tenant, warehouse) => {
  const grnType = grn?.grn_type ?? getGrnTypeFromDetail(grn);
  const isGatePassGrn = grnType === GRN_TYPE.GATE_PASS;

  const sourceCode = isGatePassGrn
    ? grn?.gate_pass?.gate_pass_code
    : grn?.purchase_order?.po_code;
  const sourceDetailPath = isGatePassGrn
    ? paths.gatePasses.details(tenant, warehouse, grn?.gate_pass?.id)
    : paths.purchaseOrder.details(tenant, warehouse, grn?.purchase_order?.id);

  return { isGatePassGrn, sourceCode, sourceDetailPath };
};
